import api from './api'
import type { BackgroundJob } from './jobs'

export type LineAmountFilterOp = 'eq' | 'gt' | 'gte' | 'lt' | 'lte'

/** Client-side refine filter: pending edits vs clean rows. */
export type RowChangeFilter = 'all' | 'with_changes' | 'clean'

/** Frontend search form fields (UI-friendly names). */
export interface QboTransactionSearchCriteria {
  date_start?: string
  date_end?: string
  contact?: string[]
  description?: string
  doc_number?: string
  line_description?: string
  line_location?: string
  line_category?: string
  line_amount_op?: LineAmountFilterOp
  line_amount?: string
  memo?: string
  /** Refine-only: filter by pending *NEW edits. Not sent to the search API. */
  row_change_filter?: RowChangeFilter
  category_conflicts_mode?: boolean
  conflict_similarity?: number
}

export interface QboTransactionSearchStats {
  complete: boolean
  transactions_scanned: number
  transactions_by_type: Record<string, number>
}

export interface QboTransactionSearchRow {
  transaction_id: string
  url: string
  date: string
  type: string
  contact_name: string
  transaction_total: number | string
  memo: string
  transaction_description: string
  line_item_id: string
  line_item_category: string
  line_item_category_new?: string
  unique_vendor_category_count?: number
  doc_number?: string
  doc_number_new?: string
  line_item_description: string
  line_item_description_new?: string
  line_item_location: string
  line_item_location_new?: string
  line_item_amount: number | string
  update_status?: string
  conflict_group_id?: string | null
  conflict_group_category_count?: number | null
  conflict_group_size?: number | null
}

export interface ConflictCategorySummary {
  name: string
  row_count: number
  total_amount: number
}

export interface ConflictGroupSummary {
  group_id: string
  contact_name: string
  representative_description: string
  categories: ConflictCategorySummary[]
  row_count: number
}

export interface ConflictStats {
  groups_found: number
  rows_in_groups: number
  rows_scanned: number
}

export interface IgnoredConflictGroupEntry {
  group_id: string
  contact_name: string
  representative_description: string
}

export interface IgnoredConflictDescriptionEntry {
  contact_name: string
  normalized_description: string
  display_description: string
}

export interface CategoryConflictIgnoresPayload {
  ignored_groups: IgnoredConflictGroupEntry[]
  ignored_descriptions: IgnoredConflictDescriptionEntry[]
}

export interface StartQboTransactionSearchResponse {
  job_id: number
  status: string
  total_items: number
  message: string
}

export interface StartQboTransactionApplyResponse {
  job_id: number
  status: string
  total_items: number
  message: string
}

export interface QboTransactionApplyChange {
  transaction_id: string
  type: string
  line_item_id: string
  line_item_description?: string
  line_item_category_new?: string
  line_item_description_new?: string
  line_item_location_new?: string
  doc_number_new?: string
  update_status?: string
}

const LINE_AMOUNT_OPS: LineAmountFilterOp[] = ['eq', 'gt', 'gte', 'lt', 'lte']

function splitCommaList(value?: string): string[] {
  if (!value?.trim()) return []
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

function lineAmountApiParams(
  amount: string | undefined,
  amountOp: LineAmountFilterOp | undefined
): { line_amount?: number; line_amount_op?: LineAmountFilterOp } {
  const raw = amount?.trim()
  if (!raw || !amountOp) return {}
  const n = parseFloat(raw.replace(/,/g, '').replace(/\$/g, ''))
  if (!Number.isFinite(n)) return {}
  if (!LINE_AMOUNT_OPS.includes(amountOp)) return {}
  return { line_amount: n, line_amount_op: amountOp }
}

const CONFLICT_SIMILARITY_MIN = 70
const CONFLICT_SIMILARITY_MAX = 100
const CONFLICT_SIMILARITY_DEFAULT = 90

function clampConflictSimilarity(value: number | undefined): number {
  const n = value ?? CONFLICT_SIMILARITY_DEFAULT
  if (!Number.isFinite(n)) return CONFLICT_SIMILARITY_DEFAULT
  return Math.min(CONFLICT_SIMILARITY_MAX, Math.max(CONFLICT_SIMILARITY_MIN, Math.round(n)))
}

/**
 * Build the POST /search body from UI criteria.
 * Conflict mode sends only dates + similarity + the mode flag (defense in depth
 * against Basic Search fields leaking into Inconsistent Categories searches).
 */
export function criteriaToApiPayload(criteria: QboTransactionSearchCriteria): {
  criteria: Record<string, unknown>
} {
  if (criteria.category_conflicts_mode) {
    return {
      criteria: {
        start_date: criteria.date_start?.trim() || undefined,
        end_date: criteria.date_end?.trim() || undefined,
        category_conflicts_mode: true,
        conflict_similarity: clampConflictSimilarity(criteria.conflict_similarity),
      },
    }
  }

  return {
    criteria: {
      start_date: criteria.date_start?.trim() || undefined,
      end_date: criteria.date_end?.trim() || undefined,
      ref_number: criteria.doc_number?.trim() || undefined,
      contacts: (criteria.contact ?? []).map((c) => c.trim()).filter(Boolean),
      ...lineAmountApiParams(criteria.line_amount, criteria.line_amount_op),
      memo: criteria.memo?.trim() || undefined,
      description: criteria.description?.trim() || undefined,
      line_item_categories: splitCommaList(criteria.line_category),
      line_item_description: criteria.line_description?.trim() || undefined,
      location_names: splitCommaList(criteria.line_location),
      category_conflicts_mode: false,
      conflict_similarity: clampConflictSimilarity(criteria.conflict_similarity),
    },
  }
}

export async function startQboTransactionSearch(
  criteria: QboTransactionSearchCriteria
): Promise<StartQboTransactionSearchResponse> {
  const response = await api.post<StartQboTransactionSearchResponse>(
    '/qbo-transaction-search/search',
    criteriaToApiPayload(criteria)
  )
  return response.data
}

export async function startQboTransactionApply(
  changes: QboTransactionApplyChange[],
  dryRun: boolean
): Promise<StartQboTransactionApplyResponse> {
  const response = await api.post<StartQboTransactionApplyResponse>(
    '/qbo-transaction-search/apply',
    {
      edits: changes,
      dry_run: dryRun,
      skip_terminal: true,
    }
  )
  return response.data
}

export function getSearchResultsFromJob(job: BackgroundJob): QboTransactionSearchRow[] {
  const rd = job.result_data as Record<string, unknown> | undefined
  const rows = rd?.rows
  return Array.isArray(rows) ? (rows as QboTransactionSearchRow[]) : []
}

export function getSearchStatsFromJob(job: BackgroundJob): QboTransactionSearchStats | null {
  const rd = job.result_data as Record<string, unknown> | undefined
  const stats = rd?.search_stats
  if (!stats || typeof stats !== 'object') return null
  const raw = stats as Record<string, unknown>
  const transactionsByType = raw.transactions_by_type
  if (
    raw.complete !== true ||
    typeof raw.transactions_scanned !== 'number' ||
    !transactionsByType ||
    typeof transactionsByType !== 'object'
  ) {
    return null
  }
  return {
    complete: true,
    transactions_scanned: raw.transactions_scanned,
    transactions_by_type: transactionsByType as Record<string, number>,
  }
}

function parseConflictCategorySummary(value: unknown): ConflictCategorySummary | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  if (
    typeof raw.name !== 'string' ||
    typeof raw.row_count !== 'number' ||
    typeof raw.total_amount !== 'number'
  ) {
    return null
  }
  return {
    name: raw.name,
    row_count: raw.row_count,
    total_amount: raw.total_amount,
  }
}

function parseConflictGroupSummary(value: unknown): ConflictGroupSummary | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  if (
    typeof raw.group_id !== 'string' ||
    typeof raw.contact_name !== 'string' ||
    typeof raw.representative_description !== 'string' ||
    typeof raw.row_count !== 'number' ||
    !Array.isArray(raw.categories)
  ) {
    return null
  }
  const categories: ConflictCategorySummary[] = []
  for (const item of raw.categories) {
    const parsed = parseConflictCategorySummary(item)
    if (!parsed) return null
    categories.push(parsed)
  }
  return {
    group_id: raw.group_id,
    contact_name: raw.contact_name,
    representative_description: raw.representative_description,
    categories,
    row_count: raw.row_count,
  }
}

export function getConflictGroupsFromJob(job: BackgroundJob): ConflictGroupSummary[] | null {
  const rd = job.result_data as Record<string, unknown> | undefined
  const groups = rd?.conflict_groups
  if (!Array.isArray(groups)) return null
  const parsed: ConflictGroupSummary[] = []
  for (const item of groups) {
    const group = parseConflictGroupSummary(item)
    if (!group) return null
    parsed.push(group)
  }
  return parsed
}

export function getConflictStatsFromJob(job: BackgroundJob): ConflictStats | null {
  const rd = job.result_data as Record<string, unknown> | undefined
  const stats = rd?.conflict_stats
  if (!stats || typeof stats !== 'object') return null
  const raw = stats as Record<string, unknown>
  if (
    typeof raw.groups_found !== 'number' ||
    typeof raw.rows_in_groups !== 'number' ||
    typeof raw.rows_scanned !== 'number'
  ) {
    return null
  }
  return {
    groups_found: raw.groups_found,
    rows_in_groups: raw.rows_in_groups,
    rows_scanned: raw.rows_scanned,
  }
}

export async function getCategoryConflictIgnores(): Promise<CategoryConflictIgnoresPayload> {
  const response = await api.get<CategoryConflictIgnoresPayload>(
    '/qbo-transaction-search/category-conflict-ignores'
  )
  return response.data
}

export async function putCategoryConflictIgnores(
  payload: CategoryConflictIgnoresPayload
): Promise<CategoryConflictIgnoresPayload> {
  const response = await api.put<CategoryConflictIgnoresPayload>(
    '/qbo-transaction-search/category-conflict-ignores',
    payload
  )
  return response.data
}

export function getApplyResultsFromJob(
  job: BackgroundJob
): Record<string, string> | undefined {
  const rd = job.result_data as Record<string, unknown> | undefined
  const txnResults = rd?.transaction_results
  if (!Array.isArray(txnResults)) return undefined

  const statuses: Record<string, string> = {}
  for (const item of txnResults) {
    if (!item || typeof item !== 'object') continue
    const txnId = (item as Record<string, unknown>).transaction_id
    const status = (item as Record<string, unknown>).update_status
    if (typeof txnId === 'string' && typeof status === 'string') {
      statuses[txnId] = status
    }
  }
  return Object.keys(statuses).length > 0 ? statuses : undefined
}
